import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../AuthContext';
import { useToast } from '../../ToastContext';
import { CarForm, validateCar, emptyCar, fromInventory } from '../../components/CarForm';
import { CarPicker } from '../../components/CarPicker';
import { FLOW_OPTIONS } from '../../stepFlows';

function validateDeal(deal) {
  const e = {};
  if (!deal.start_date) e.start_date = 'Start date is required.';
  if (!deal.end_date) e.end_date = 'End date is required.';
  else if (deal.start_date && new Date(deal.start_date) >= new Date(deal.end_date))
    e.end_date = 'End date must be after start date.';
  if (deal.bulk_discount_percent !== '' && deal.bulk_discount_percent != null &&
      (isNaN(deal.bulk_discount_percent) || Number(deal.bulk_discount_percent) < 0 || Number(deal.bulk_discount_percent) > 100))
    e.bulk_discount_percent = 'Must be a number between 0 and 100.';
  if (deal.inspection_window !== '' && deal.inspection_window != null &&
      (isNaN(deal.inspection_window) || Number(deal.inspection_window) < 1))
    e.inspection_window = 'Must be at least 1 day.';
  if (!deal.funding_time?.trim())
    e.funding_time = 'Funding time is required.';
  return e;
}

function hasErrors(errs) { return Object.keys(errs).length > 0; }

function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <p className="mt-1 text-xs text-red-600 flex items-start gap-1">
      <svg className="w-3 h-3 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
      {msg}
    </p>
  );
}

function AddRecipientsModal({ dealName, available, onAdd, onClose }) {
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const confirm = async () => {
    if (!selected.length) return;
    setSaving(true);
    try { await onAdd(selected); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-sm flex flex-col max-h-[85dvh]">
        <div className="p-5 border-b border-slate-200 dark:border-slate-700 shrink-0">
          <h2 className="text-base font-semibold text-slate-900 dark:text-white">Add Recipients</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Select additional buyer lots to receive <span className="font-medium">{dealName || 'this deal'}</span>.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-1.5">
          {available.length === 0 ? (
            <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-8">
              All connected buyer lots have already received this deal.
            </p>
          ) : available.map(loc => (
            <div key={loc.id}
              onClick={() => toggle(loc.id)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded border transition-colors cursor-pointer ${
                selected.includes(loc.id)
                  ? 'border-blue-400 bg-blue-50 dark:border-blue-600 dark:bg-blue-900/20'
                  : 'border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700/50'
              }`}>
              <input type="checkbox" readOnly checked={selected.includes(loc.id)}
                className="w-4 h-4 accent-blue-600 pointer-events-none shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{loc.name}</p>
                {loc.account_name && <p className="text-xs text-slate-400 truncate">{loc.account_name}</p>}
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2 shrink-0">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={confirm} disabled={!selected.length || saving} className="btn-primary disabled:opacity-50">
            {saving
              ? <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin inline-block mr-1.5" />Sending…</>
              : `Send to ${selected.length || 0} Lot${selected.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function SendDealModal({ dealName, locations, onSend, onClose }) {
  const [selected, setSelected] = useState([]);
  const navigate = useNavigate();

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md flex flex-col max-h-[85dvh]">
        <div className="p-5 border-b border-slate-200 dark:border-slate-700 shrink-0">
          <h2 className="text-base font-semibold text-slate-900 dark:text-white">Send {dealName || 'Deal'}</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Select which buyer lots receive this deal.</p>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-1.5">
          {locations.length === 0 ? (
            <div className="text-center py-8 space-y-2">
              <p className="text-sm text-slate-400 dark:text-slate-500">No buyer lots connected yet.</p>
              <button onClick={() => { onClose(); navigate('/settings'); }}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
                Manage connections in Account Settings →
              </button>
            </div>
          ) : locations.map(loc => (
            <div key={loc.id} className={`flex items-center gap-3 px-3 py-2.5 rounded border transition-colors cursor-pointer ${
              selected.includes(loc.id)
                ? 'border-blue-400 bg-blue-50 dark:border-blue-600 dark:bg-blue-900/20'
                : 'border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700/50'
            }`} onClick={() => setSelected(s => s.includes(loc.id) ? s.filter(x => x !== loc.id) : [...s, loc.id])}>
              <input type="checkbox" readOnly checked={selected.includes(loc.id)}
                className="w-4 h-4 accent-blue-600 pointer-events-none shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{loc.name}</p>
                {loc.account_name && <p className="text-xs text-slate-400 truncate">{loc.account_name}</p>}
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2 shrink-0">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={() => onSend(selected)} disabled={!selected.length} className="btn-primary disabled:opacity-50">
            Send to {selected.length || 0} Lot{selected.length !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DealEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { appUser } = useAuth();
  const toast = useToast();
  const defaultLocationId = appUser?.location_id ?? null;

  const [deal, setDeal] = useState({
    name: '', start_date: '', end_date: '', bulk_discount_percent: 0,
    pickup_or_delivery: 'pickup', inspection_window: 7,
    transaction_type: 'Title for Dollar', funding_time: '', status: 'draft',
    step_flow: 'pay_first',
  });
  const [distributedTo, setDistributedTo] = useState([]);
  const [editingName, setEditingName] = useState(false);
  const [cars, setCars]           = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [dealId, setDealId]       = useState(id || null);
  const [saving, setSaving]       = useState(false);
  const [notFound, setNotFound]   = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [showSend, setShowSend]           = useState(false);
  const [showAddRecipients, setShowAddRecipients] = useState(false);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [buyerLocations, setBuyerLocations] = useState([]);

  const [dealErrors, setDealErrors] = useState({});
  const [carErrors, setCarErrors]   = useState({});
  const [locationDefaults, setLocationDefaults] = useState({});

  const tabsRef = useRef(null);

  useEffect(() => {
    if (defaultLocationId) {
      api.get(`/locations/${defaultLocationId}`)
        .then(loc => setLocationDefaults({
          default_tax_rate: loc.default_tax_rate,
          default_seller_disclosure: loc.default_seller_disclosure,
        }))
        .catch(() => {});
    }
  }, [defaultLocationId]);

  useEffect(() => {
    setNotFound(false);
    if (id) {
      api.get(`/deals/${id}`)
        .then(({ cars: dc, distributed_to, ...fields }) => {
          setDeal({
            name: fields.name || '',
            start_date: fields.start_date?.slice(0, 10) || '',
            end_date: fields.end_date?.slice(0, 10) || '',
            bulk_discount_percent: fields.bulk_discount_percent ?? 0,
            pickup_or_delivery: fields.pickup_or_delivery || 'pickup',
            inspection_window: fields.inspection_window ?? '',
            transaction_type: fields.transaction_type || 'Title for Dollar',
            funding_time: fields.funding_time || '',
            status: fields.status || 'draft',
            step_flow: fields.step_flow || 'pay_first',
          });
          setDistributedTo(distributed_to || []);
          setCars(dc?.length
            ? dc.map(c => ({
                ...emptyCar(), ...c, _key: c.id, _needsAssociation: false,
                safety_exp: c.safety_exp?.slice(0, 7) || '',
                reg_exp: c.reg_exp?.slice(0, 7) || '',
                buy_now_price: c.buy_now_price ?? '',
                reserve_price: c.reserve_price ?? '',
                mileage: c.mileage ?? '',
                year: c.year ?? '',
              }))
            : [emptyCar(defaultLocationId, locationDefaults)]
          );
        })
        .catch(() => setNotFound(true));
    } else {
      setCars([emptyCar(defaultLocationId, locationDefaults)]);
    }
    // locationDefaults intentionally excluded from deps — it loads async shortly after
    // mount, and re-running this effect would wipe out any cars already added by then.
  }, [id, defaultLocationId]);

  const setDealField = f => e => {
    setDeal(d => ({ ...d, [f]: e.target.value }));
    if (dealErrors[f]) setDealErrors(e => { const n = { ...e }; delete n[f]; return n; });
  };

  const updateCar = (idx, updated, changedField) => {
    setCars(c => c.map((car, i) => i === idx ? updated : car));
    if (changedField) {
      const key = cars[idx]._key;
      setCarErrors(e => {
        if (!e[key]?.[changedField]) return e;
        const carE = { ...e[key] };
        delete carE[changedField];
        return { ...e, [key]: carE };
      });
    }
  };

  const existingCarIds = new Set(cars.filter(c => c.id).map(c => c.id));

  const addExistingCar = (inventoryCar) => {
    const car = fromInventory(inventoryCar);
    const next = [...cars, car];
    setCars(next);
    setActiveIdx(next.length - 1);
    setShowPicker(false);
    setTimeout(() => tabsRef.current?.scrollTo({ left: 9999, behavior: 'smooth' }), 50);
  };

  const addNewCar = () => {
    const next = [...cars, emptyCar(defaultLocationId, locationDefaults)];
    setCars(next);
    setActiveIdx(next.length - 1);
    setShowPicker(false);
    setTimeout(() => tabsRef.current?.scrollTo({ left: 9999, behavior: 'smooth' }), 50);
  };

  const removeCar = async (idx) => {
    const car = cars[idx];
    // Only call the association-delete API if this car was actually associated with
    // the deal server-side — cars just added via the picker aren't linked until the
    // next save (_needsAssociation), so there's nothing to delete yet (B1).
    if (car.id && dealId && !car._needsAssociation) await api.del(`/deals/${dealId}/cars/${car.id}`);
    const next = cars.filter((_, i) => i !== idx);
    const newCars = next.length ? next : [emptyCar(defaultLocationId)];
    setCars(newCars);
    setActiveIdx(Math.min(activeIdx, newCars.length - 1));
  };

  const runValidation = () => {
    const dErrs = validateDeal(deal);
    const cErrs = {};
    const vinCounts = {};
    cars.forEach(car => {
      const vin = car.vin?.trim().toLowerCase();
      if (vin) vinCounts[vin] = (vinCounts[vin] || 0) + 1;
    });
    cars.forEach(car => {
      const e = validateCar(car);
      const vin = car.vin?.trim().toLowerCase();
      if (vin && vinCounts[vin] > 1) e.vin = 'Duplicate VIN — this VIN is used by another car in this deal.';
      if (hasErrors(e)) cErrs[car._key] = e;
    });
    setDealErrors(dErrs);
    setCarErrors(cErrs);
    const totalErrors = Object.keys(dErrs).length + Object.keys(cErrs).length;
    if (totalErrors > 0) {
      const firstBadIdx = cars.findIndex(c => cErrs[c._key]);
      if (firstBadIdx !== -1) setActiveIdx(firstBadIdx);
      return false;
    }
    return true;
  };

  const performSave = async () => {
    setSaving(true);
    try {
      let sid = dealId;
      if (!sid) {
        let s;
        try {
          s = await api.post('/deals', { ...deal, status: 'draft' });
        } catch (err) {
          toast.error(`Failed to save deal: ${err.message}`);
          return null;
        }
        sid = s.uuid;
        setDealId(sid);
        setDeal(d => ({ ...d, name: s.name }));
      } else {
        try {
          await api.put(`/deals/${sid}`, deal);
        } catch (err) {
          toast.error(`Failed to save deal: ${err.message}`);
          return null;
        }
      }
      const updated = [];
      for (let i = 0; i < cars.length; i++) {
        const car = cars[i];
        const carPayload = {
          ...car,
          location_id: car.location_id || defaultLocationId,
          safety_exp: car.safety_exp ? `${car.safety_exp}-01` : null,
          reg_exp: car.reg_exp ? `${car.reg_exp}-01` : null,
        };
        try {
          if (!car.id) {
            const s = await api.post('/cars', carPayload);
            await api.post(`/deals/${sid}/cars`, { car_id: s.id });
            updated.push({ ...car, id: s.id, _key: s.id, _needsAssociation: false });
          } else {
            await api.put(`/cars/${car.id}`, carPayload);
            if (car._needsAssociation) await api.post(`/deals/${sid}/cars`, { car_id: car.id });
            updated.push({ ...car, _needsAssociation: false });
          }
        } catch (err) {
          const label = [car.year, car.make, car.model].filter(Boolean).join(' ') || car.vin || `Car ${i + 1}`;
          toast.error(`${label}: ${err.message}`);
          if (err.message.toLowerCase().includes('vin')) {
            setCarErrors(e => ({ ...e, [car._key]: { ...e[car._key], vin: err.message } }));
            setActiveIdx(i);
          }
          return null;
        }
      }
      setCars(updated);
      return sid;
    } finally {
      setSaving(false);
    }
  };

  const submitInFlightRef = useRef(false);

  const saveDraft = async () => {
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    try {
      if (!runValidation()) return;
      const sid = await performSave();
      if (sid) navigate('/seller/deals');
    } finally {
      submitInFlightRef.current = false;
    }
  };

  const [unsending, setUnsending] = useState(false);
  const [resending, setResending] = useState(false);

  const openSend = async () => {
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    try {
      if (!runValidation()) return;
      const sid = await performSave();
      if (!sid) return;
      // Previously sent, then unsent for edits — resend to the same lots, no need to
      // reselect recipients.
      if (distributedTo.length > 0) {
        setResending(true);
        try {
          const updated = await api.post(`/deals/${sid}/resend`, {});
          setDeal(d => ({ ...d, status: updated.status }));
          navigate('/seller/deals');
        } catch (err) {
          toast.error(err.message);
        } finally {
          setResending(false);
        }
        return;
      }
      const locs = await api.get(`/location-relationships?seller_location_id=${defaultLocationId}`);
      setBuyerLocations(locs);
      setShowSend(true);
    } finally {
      submitInFlightRef.current = false;
    }
  };

  const unsendDeal = async () => {
    setUnsending(true);
    try {
      const updated = await api.post(`/deals/${dealId}/unsend`, {});
      setDeal(d => ({ ...d, status: updated.status }));
      toast.success('Deal pulled back to draft — make your edits, then resend.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUnsending(false);
    }
  };

  const confirmSend = async (selectedLocations) => {
    if (!selectedLocations.length) return;
    await api.post(`/deals/${dealId}/distribute`, { location_ids: selectedLocations });
    setShowSend(false);
    navigate('/seller/deals');
  };

  const openAddRecipients = async () => {
    const allRelationships = await api.get(`/location-relationships?seller_location_id=${defaultLocationId}`);
    const alreadySentIds = new Set(distributedTo.map(r => r.id));
    setAvailableLocations(allRelationships.filter(l => !alreadySentIds.has(l.id)));
    setShowAddRecipients(true);
  };

  const addMoreRecipients = async (selectedLocationIds) => {
    await api.post(`/deals/${dealId}/distribute`, { location_ids: selectedLocationIds });
    const newRecipients = availableLocations.filter(l => selectedLocationIds.includes(l.id));
    setDistributedTo(prev => [...prev, ...newRecipients.map(l => ({ id: l.id, name: l.name, account_name: l.account_name }))]);
    setShowAddRecipients(false);
  };

  const saveName = async () => {
    if (!dealId || !deal.name) { setEditingName(false); return; }
    try {
      await api.put(`/deals/${dealId}`, { name: deal.name });
      setEditingName(false);
    } catch (err) {
      toast.error(`Failed to rename deal: ${err.message}`);
    }
  };

  if (notFound) return (
    <div className="flex flex-col items-center justify-center h-full py-24 text-center px-6">
      <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h2 className="text-base font-semibold text-slate-700 dark:text-slate-300 mb-1">Deal not found</h2>
      <p className="text-sm text-slate-400 dark:text-slate-500 mb-5">This deal doesn't exist or you don't have access to it.</p>
      <button onClick={() => navigate('/seller/deals')} className="btn-primary text-sm px-4 py-2">Back to deals</button>
    </div>
  );

  const activeCar   = cars[activeIdx] ?? cars[0];
  const totalErrors = Object.keys(dealErrors).length + Object.keys(carErrors).length;
  const locked      = !!dealId && deal.status !== 'draft';
  const tabHasError = (car) => hasErrors(carErrors[car._key] ?? {});

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex-1 min-w-0 w-full sm:w-auto sm:mr-4">
          <button onClick={() => navigate('/seller/deals')}
            className="text-sm text-slate-400 hover:text-slate-600 flex items-center gap-1 mb-2 transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to deals
          </button>
          {editingName ? (
            <input autoFocus type="text" value={deal.name}
              onChange={e => setDeal(d => ({ ...d, name: e.target.value }))}
              onBlur={saveName}
              onKeyDown={e => e.key === 'Enter' && e.target.blur()}
              className="text-2xl font-bold bg-transparent border-b-2 border-blue-400 focus:outline-none text-slate-900 dark:text-white w-full"
            />
          ) : (
            <button onClick={() => setEditingName(true)} className="flex items-center gap-2 group text-left min-w-0 w-full">
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white truncate min-w-0">
                {deal.name || (dealId ? `Deal #${dealId}` : 'New Deal')}
              </h1>
              <svg className="w-4 h-4 text-slate-300 group-hover:text-slate-500 shrink-0 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
          {!locked && (
            <>
              <button onClick={saveDraft} disabled={saving} className="btn-secondary flex-1 sm:flex-initial flex items-center gap-2">
                {saving
                  ? <><div className="w-3.5 h-3.5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />Saving…</>
                  : 'Save Draft'}
              </button>
              <button onClick={openSend} disabled={saving || resending} className="btn-primary flex-1 sm:flex-initial">
                {resending ? 'Resending…' : distributedTo.length > 0 ? 'Resend Deal →' : 'Send Deal →'}
              </button>
            </>
          )}
          {locked && (
            <>
              <button
                onClick={unsendDeal}
                disabled={unsending}
                className="px-3 py-1.5 text-xs font-semibold text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-50 transition-colors disabled:opacity-50"
              >
                {unsending ? 'Un-sending…' : 'Unsend for Editing'}
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm('Delete this deal? This cannot be undone.')) return;
                  try {
                    await api.del(`/deals/${dealId}`);
                    navigate('/seller/deals');
                  } catch (err) {
                    toast.error(`Failed to delete deal: ${err.message}`);
                  }
                }}
                className="px-3 py-1.5 text-xs font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
              >
                Delete Deal
              </button>
            </>
          )}
        </div>
      </div>

      {locked && (
        <div className="mb-5 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl flex items-start gap-3">
          <svg className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">This deal has been sent and is locked.</p>
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
              You can rename it above. To fix a mistake, use "Unsend for Editing" — this is only available
              until a buyer makes an offer or buys a car, at which point it's locked for good.
            </p>
            {distributedTo.length > 0 && (
              <div className="mt-2">
                <div className="flex items-center gap-3">
                  <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">Sent to:</p>
                  <button
                    onClick={openAddRecipients}
                    className="text-xs font-medium text-amber-700 dark:text-amber-400 underline underline-offset-2 hover:text-amber-900 dark:hover:text-amber-200 transition-colors"
                  >
                    + Add more
                  </button>
                </div>
                <ul className="mt-1 space-y-0.5">
                  {distributedTo.map(r => (
                    <li key={r.id} className="text-xs text-amber-700 dark:text-amber-400">
                      {r.name}{r.account_name ? ` (${r.account_name})` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {totalErrors > 0 && (
        <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
          <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-red-800">
              {totalErrors} field{totalErrors !== 1 ? 's need' : ' needs'} correction before saving.
            </p>
            <p className="text-xs text-red-600 mt-0.5">Fix the highlighted fields below, then try again.</p>
          </div>
        </div>
      )}

      <div className="card p-5 mb-5">
        <p className="section-title">Deal Info</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className="label">Start Date <span className="text-red-500">*</span></label>
            <input className={`input${dealErrors.start_date ? ' border-red-400 focus:ring-red-500' : ''}`}
              type="date" value={deal.start_date} onChange={setDealField('start_date')} disabled={locked} />
            <FieldError msg={dealErrors.start_date} />
          </div>
          <div>
            <label className="label">End Date <span className="text-red-500">*</span></label>
            <input className={`input${dealErrors.end_date ? ' border-red-400 focus:ring-red-500' : ''}`}
              type="date" value={deal.end_date} onChange={setDealField('end_date')} disabled={locked} />
            <FieldError msg={dealErrors.end_date} />
          </div>
          <div>
            <label className="label">Bulk Discount %</label>
            <div className="relative">
              <input className={`${dealErrors.bulk_discount_percent ? 'input border-red-400 focus:ring-red-500' : 'input'} pr-7`}
                type="number" min="0" max="100" value={deal.bulk_discount_percent}
                onChange={setDealField('bulk_discount_percent')} placeholder="0" disabled={locked} />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
            </div>
            <FieldError msg={dealErrors.bulk_discount_percent} />
            <p className="mt-1 text-xs text-slate-400">
              Applied to the total only while none of this deal's cars have been purchased.
            </p>
          </div>
          <div>
            <label className="label">Pickup / Delivery <span className="text-red-500">*</span></label>
            <select className="input" value={deal.pickup_or_delivery} onChange={setDealField('pickup_or_delivery')} disabled={locked}>
              <option value="pickup">Pickup</option>
              <option value="delivery">Delivery</option>
            </select>
          </div>
          <div>
            <label className="label">Inspection Window (days)</label>
            <input className={`input${dealErrors.inspection_window ? ' border-red-400 focus:ring-red-500' : ''}`}
              type="number" value={deal.inspection_window} onChange={setDealField('inspection_window')} disabled={locked} />
            <FieldError msg={dealErrors.inspection_window} />
          </div>
          <div>
            <label className="label">Transaction Type <span className="text-red-500">*</span></label>
            <select className="input" value={deal.transaction_type} onChange={setDealField('transaction_type')} disabled={locked}>
              <option>Title for Dollar</option>
              <option>Title Incoming</option>
            </select>
          </div>
          <div>
            <label className="label">Funding Time <span className="text-red-500">*</span></label>
            <input className={`input${dealErrors.funding_time ? ' border-red-400 focus:ring-red-500' : ''}`}
              type="text" value={deal.funding_time} onChange={setDealField('funding_time')}
              placeholder="e.g. 48 hours" disabled={locked} />
            <FieldError msg={dealErrors.funding_time} />
          </div>
          <div>
            <label className="label">Transaction Steps <span className="text-red-500">*</span></label>
            <select className="input" value={deal.step_flow} onChange={setDealField('step_flow')} disabled={locked}>
              {FLOW_OPTIONS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
            <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">Sets the order of payment, inspection, and pickup for every sale on this deal.</p>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div ref={tabsRef} className="flex overflow-x-auto border-b border-slate-200 bg-slate-50 scrollbar-hide">
          {cars.map((car, idx) => {
            const label = car.year && car.make ? `${car.year} ${car.make}` : (car.vin || `Car ${idx + 1}`);
            const isActive = activeIdx === idx;
            const hasErr = tabHasError(car);
            return (
              <button key={car._key} onClick={() => setActiveIdx(idx)}
                className={`group relative flex items-center gap-2 px-5 py-3 text-sm font-medium whitespace-nowrap border-r border-slate-200 transition-colors ${
                  isActive ? 'bg-white text-blue-600' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
                }`}>
                {isActive && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />}
                {hasErr && <div className="w-2 h-2 rounded-full bg-red-500 shrink-0" />}
                <span>{label}</span>
                {car.year && car.make && car.vin && (
                  <span className="text-xs text-slate-400 font-normal">{car.vin}</span>
                )}
                {!locked && (
                  <span onClick={e => { e.stopPropagation(); removeCar(idx); }}
                    className="w-4 h-4 flex items-center justify-center rounded text-slate-300 hover:text-red-400 hover:bg-red-50 transition-colors">×</span>
                )}
              </button>
            );
          })}
          {!locked && (
            <button onClick={() => setShowPicker(true)}
              className="px-5 py-3 text-sm font-semibold text-blue-600 hover:bg-blue-50 whitespace-nowrap transition-colors flex items-center gap-1.5 border-r border-slate-200">
              <span className="text-lg leading-none">+</span> Add Car
            </button>
          )}
        </div>

        <div className="p-6">
          {cars.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <p className="text-sm mb-3">No cars added yet.</p>
              <button onClick={() => setShowPicker(true)} className="btn-primary text-sm">+ Add Car</button>
            </div>
          ) : activeCar ? (
            <>
              {activeCar.id && (
                <div className="flex items-center gap-2 mb-5 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <svg className="w-4 h-4 text-blue-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-xs text-blue-700">
                    {activeCar.vin ? `VIN ${activeCar.vin}` : `Car #${activeCar.id}`} — from inventory, edits will update the inventory record when saved.
                  </p>
                </div>
              )}
              <CarForm
                car={activeCar}
                errors={carErrors[activeCar._key] ?? {}}
                onChange={(updated, changedField) => updateCar(activeIdx, updated, changedField)}
                onUploadError={(msg) => toast.error(msg)}
                disabled={locked}
              />
            </>
          ) : null}
        </div>
      </div>

      {showPicker && (
        <CarPicker
          existingCarIds={existingCarIds}
          locationId={defaultLocationId}
          onAddExisting={addExistingCar}
          onAddNew={addNewCar}
          onClose={() => setShowPicker(false)}
        />
      )}

      {showAddRecipients && (
        <AddRecipientsModal
          dealName={deal.name}
          available={availableLocations}
          onAdd={addMoreRecipients}
          onClose={() => setShowAddRecipients(false)}
        />
      )}

      {showSend && (
        <SendDealModal
          dealName={deal.name}
          locations={buyerLocations}
          onSend={confirmSend}
          onClose={() => setShowSend(false)}
        />
      )}
    </div>
  );
}
